/**
 * Servicio para obtener precios estacionales del backend
 */

import { buildApiUrl } from '../config/api';
import { notifySupportApiNetworkError, notifySupportApiServerError } from '../utils/supportApiErrorNotifier';

/**
 * Obtiene el precio para un modelo en una fecha específica
 * @param {number} vehicleModelId - ID del modelo de vehículo
 * @param {Date|string} date - Fecha para la cual obtener el precio
 * @returns {Promise<Object|null>} Precio estacional o null si no existe
 */
export const getPriceForDate = async (vehicleModelId, date) => {
  try {
    // Validar que el vehicleModelId sea válido
    if (!vehicleModelId || isNaN(vehicleModelId)) {
      console.warn('ID de modelo inválido:', vehicleModelId);
      return null;
    }

    const dateStr = typeof date === 'string' ? date : date.toISOString().split('T')[0];
    const url = buildApiUrl(`/api/pricing/price-for-date/${vehicleModelId}?date=${dateStr}`);
    
    const response = await fetch(url);
    
    if (!response.ok) {
      notifySupportApiServerError(response.status);
      if (response.status === 404) {
        // No hay precio estacional para esta fecha
        return null;
      }
      if (response.status === 500) {
        // Error del servidor, probablemente modelo no encontrado
        console.warn(`Modelo ${vehicleModelId} no encontrado en el backend`);
        return null;
      }
      throw new Error(`Error ${response.status}: ${response.statusText}`);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    notifySupportApiNetworkError(error);
    console.error('Error obteniendo precio estacional:', error);
    return null;
  }
};

/**
 * Calcula el precio de una reserva usando precios estacionales
 * @param {number} vehicleModelId - ID del modelo de vehículo
 * @param {Date|string} startDate - Fecha de inicio
 * @param {Date|string} endDate - Fecha de fin
 * @returns {Promise<Object>} Precio calculado con desglose
 */
export const calculateBookingPrice = async (vehicleModelId, startDate, endDate) => {
  try {
    const startDateStr = typeof startDate === 'string' ? startDate : startDate.toISOString().split('T')[0];
    const endDateStr = typeof endDate === 'string' ? endDate : endDate.toISOString().split('T')[0];
    
    const url = buildApiUrl('/api/pricing/calculate-booking');
    
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        vehicleModelId,
        startDate: startDateStr,
        endDate: endDateStr,
        useSeasonBreakdown: true
      })
    });
    
    if (!response.ok) {
      notifySupportApiServerError(response.status);
      throw new Error(`Error ${response.status}: ${response.statusText}`);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    notifySupportApiNetworkError(error);
    console.error('Error calculando precio de reserva:', error);
    throw error;
  }
};

/**
 * Obtiene las temporadas activas para una fecha
 * @param {Date|string} date - Fecha para consultar
 * @returns {Promise<Array>} Lista de temporadas activas
 */
export const getActiveSeasons = async (date) => {
  try {
    const dateStr = typeof date === 'string' ? date : date.toISOString().split('T')[0];
    const url = buildApiUrl(`/api/pricing/active-seasons?date=${dateStr}`);
    
    const response = await fetch(url);
    
    if (!response.ok) {
      notifySupportApiServerError(response.status);
      throw new Error(`Error ${response.status}: ${response.statusText}`);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    notifySupportApiNetworkError(error);
    console.error('Error obteniendo temporadas activas:', error);
    return [];
  }
};

/**
 * Obtiene todas las temporadas disponibles
 * @returns {Promise<Array>} Lista de todas las temporadas
 */
export const getAllSeasons = async () => {
  try {
    const url = buildApiUrl('/api/pricing/seasons');
    
    const response = await fetch(url);
    
    if (!response.ok) {
      notifySupportApiServerError(response.status);
      throw new Error(`Error ${response.status}: ${response.statusText}`);
    }
    
    const data = await response.json();
    return data;
  } catch (error) {
    notifySupportApiNetworkError(error);
    console.error('Error obteniendo temporadas:', error);
    return [];
  }
};

/**
 * Calcula el precio dinámico usando precios estacionales para reservas que cruzan temporadas
 * @param {Object} vehicle - Objeto del vehículo
 * @param {number} durationInDays - Duración en días
 * @param {Date|string} startDate - Fecha de inicio
 * @param {Date|string} endDate - Fecha de fin
 * @returns {Promise<Object>} Precio calculado con información de temporada
 */
export const calculateDynamicPriceWithSeasons = async (vehicle, durationInDays, startDate, endDate = null) => {
  try {
    // Si tenemos fecha de fin, usar el cálculo que cruza temporadas
    // IMPORTANTE: Usar vehicle.modelId, no vehicle.id, porque el backend espera el ID del modelo
    if (endDate) {
      const vehicleModelId = vehicle.modelId || vehicle.id; // Fallback a vehicle.id por compatibilidad
      const bookingPrice = await calculateBookingPrice(vehicleModelId, startDate, endDate);
      
      if (bookingPrice && bookingPrice.total !== undefined) {
        return {
          totalPrice: Math.round(bookingPrice.total * 100) / 100,
          basePrice: bookingPrice.breakdown?.basePrice || vehicle.basePrice,
          threeToFive: vehicle.threeToFive,
          additionalDay: vehicle.additionalDay,
          durationInDays,
          discountApplied: bookingPrice.breakdown?.discountApplied || 0,
          additionalDaysPrice: bookingPrice.breakdown?.additionalDaysPrice || 0,
          season: bookingPrice.breakdown?.season,
          pricingType: bookingPrice.breakdown?.pricingType || 'default',
          pricePerDay: Math.round((bookingPrice.total / durationInDays) * 100) / 100,
          seasonBreakdown: bookingPrice.breakdown?.seasonBreakdown || []
        };
      }
    }
    
    // Fallback: Intentar obtener precio estacional solo para la fecha de inicio
    // IMPORTANTE: Usar vehicle.modelId, no vehicle.id, porque el backend espera el ID del modelo
    const vehicleModelId = vehicle.modelId || vehicle.id; // Fallback a vehicle.id por compatibilidad
    const seasonalPricing = await getPriceForDate(vehicleModelId, startDate);
    
    let pricing;
    let seasonInfo = null;
    
    if (seasonalPricing && seasonalPricing.pricingType === 'seasonal') {
      // Usar precios estacionales
      pricing = {
        basePrice: seasonalPricing.basePrice,
        threeToFive: seasonalPricing.threeToFive,
        additionalDay: seasonalPricing.additionalDay
      };
      seasonInfo = seasonalPricing.season;
    } else {
      // Usar precios por defecto del modelo
      pricing = {
        basePrice: vehicle.basePrice,
        threeToFive: vehicle.threeToFive,
        additionalDay: vehicle.additionalDay
      };
    }
    
    // Calcular precio según duración
    let totalPrice = 0;
    let discountApplied = 0;
    let additionalDaysPrice = 0;
    
    if (durationInDays <= 2) {
      // Precio normal para 1-2 días
      totalPrice = pricing.basePrice * durationInDays;
    } else if (durationInDays <= 5) {
      // Aplicar descuento para 3-5 días
      const baseTotal = pricing.basePrice * durationInDays;
      discountApplied = baseTotal * (pricing.threeToFive / 100);
      totalPrice = baseTotal - discountApplied;
    } else {
      // Precio especial para más de 5 días
      const first5Days = pricing.basePrice * 5;
      const first5DaysDiscount = first5Days * (pricing.threeToFive / 100);
      const remainingDays = durationInDays - 5;
      additionalDaysPrice = pricing.additionalDay * remainingDays;
      
      totalPrice = (first5Days - first5DaysDiscount) + additionalDaysPrice;
      discountApplied = first5DaysDiscount;
    }
    
    return {
      totalPrice: Math.round(totalPrice * 100) / 100,
      basePrice: pricing.basePrice,
      threeToFive: pricing.threeToFive,
      additionalDay: pricing.additionalDay,
      durationInDays,
      discountApplied,
      additionalDaysPrice,
      season: seasonInfo,
      pricingType: seasonalPricing ? 'seasonal' : 'default',
      pricePerDay: Math.round((totalPrice / durationInDays) * 100) / 100
    };
  } catch (error) {
    console.error('Error calculando precio dinámico con temporadas:', error);
    
    // Fallback a cálculo local
    return {
      totalPrice: 0,
      basePrice: vehicle.basePrice,
      threeToFive: vehicle.threeToFive,
      additionalDay: vehicle.additionalDay,
      durationInDays,
      discountApplied: 0,
      additionalDaysPrice: 0,
      season: null,
      pricingType: 'default',
      pricePerDay: 0,
      error: 'Error calculando precios estacionales'
    };
  }
};
